import {
    GetInvoiceDebtListParams,
    GetMarketInfoCollectionParams,
    GetMarketInventoryGetParams,
    PatchMarketInventorySetParams,
    PatchMarketSetShelfSizeParams,
    PostGeoAddParams,
    PostInvoiceMakePaymentParams,
    PostMarketChangeGeoParams,
    PostOrderCreateParams,
    PostOrderSaveOneParams,
    PostOrderSaveParams,
    PostTaskAddDebtTaskParams,
    PostTaskAddDistributionPricesParams,
    PostTaskAddRejectTaskParams,
    PostTaskAddSaleTaskParams,
    PostTaskCompleteParams,
} from "tm-shared/api";

import { QueueProgress, QueueTaskStatus } from "@/hooks/useAsyncTaskQueue";

export type QueueCommandMap = {
    postOrderCreate: PostOrderCreateParams;
    postOrderSave: PostOrderSaveParams;
    postOrderSaveOne: PostOrderSaveOneParams;
    postMarketChangeGeo: PostMarketChangeGeoParams;
    postTaskAddSaleTask: PostTaskAddSaleTaskParams;
    getMarketInventoryGetThunk: GetMarketInventoryGetParams;
    postGeoAdd: PostGeoAddParams;
    postTaskComplete: PostTaskCompleteParams;
    postAddDistributionPrices: PostTaskAddDistributionPricesParams;
    postTaskAddDebtTask: PostTaskAddDebtTaskParams;
    postInvoiceMakePayment: PostInvoiceMakePaymentParams;
    getInvoiceDebtListThunk: GetInvoiceDebtListParams;
    getMarketInfoCollectionWorkThunk: GetMarketInfoCollectionParams;
    postTaskAddRejectTask: PostTaskAddRejectTaskParams;
    patchMarketSetShelfSize: PatchMarketSetShelfSizeParams;
    putMarketChangePhoto: { photoId: string; marketId: string };
    patchMarketInventorySet: PatchMarketInventorySetParams;
    postFileUpload: string;
    updatePhotoStorage: string[];
    deletePhotoFromStorage: string;
    updateCheckout: null;
    getTaskListsThunk: null;
};

export type QueueCommandType = keyof QueueCommandMap;

export const QueueCommandMethods: Partial<Record<QueueCommandType, string>> = {
    postOrderCreate: "order.create",
    postOrderSave: "order.save",
    postOrderSaveOne: "order.saveOne",
    postMarketChangeGeo: "market.changeGeo",
    postTaskAddSaleTask: "task.addSaleTask",
    getMarketInventoryGetThunk: "marketInventory.get",
    postGeoAdd: "geo.add",
    postTaskComplete: "task.complete",
    postAddDistributionPrices: "task.addDistributionPrices",
    postTaskAddDebtTask: "task.addDebtTask",
    postInvoiceMakePayment: "invoice.makePayment",
    getInvoiceDebtListThunk: "invoice.debtList",
    getMarketInfoCollectionWorkThunk: "market.infoCollection",
    postTaskAddRejectTask: "task.addRejectTask",
    patchMarketSetShelfSize: "market.setShelfSize",
    patchMarketInventorySet: "marketInventory.set",
};

export type CommandOptions = {
    stopOnError?: boolean;
    showError?: boolean;
    showSuccess?: boolean;
};

export type QueueCommand<T extends QueueCommandType = QueueCommandType> = {
    type: T;
    title: string;
    params: QueueCommandMap[T];
    method: string;
    options?: {
        stopOnError?: boolean;
        ignoreErrorCodes?: string[];
        moveToFailed?: {
            code: string;
            label: string;
        }[];
    };
};

export type CommandParams<T extends QueueCommandType> = QueueCommandMap[T];

export type AnyQueueCommand = {
    [K in QueueCommandType]: QueueCommand<K>;
}[QueueCommandType];

export interface RequestQueueItemMetadata {
    marketId?: string;
    taskId?: string;
    orderId?: string;
    title?: string;
    taskName?: string;
    marketName?: string;
}

export interface RequestQueueItem {
    id: string;
    commands: AnyQueueCommand[];
    progress?: QueueProgress;
    metadata?: RequestQueueItemMetadata;
    createdAt: string;
    status: QueueTaskStatus;
    options?: {
        stopOnError?: boolean;
        todayOnly?: boolean;
    };
}
